import { useEffect, useRef } from "react";

const DEFAULT_TITLE = "Official Oberin - Fantasy MMORPG for Mac and Windows";
const SITE_NAME = "Official Oberin";

interface SeoOptions {
    title: string;
    description: string;
    canonical?: string;
    /** When true the auto-generated canonical URL will include the current
     *  query string (e.g. ?page=2).  Has no effect when `canonical` is
     *  supplied explicitly. */
    includeQuery?: boolean;
    robots?: string;
    ogType?: string;
    ogImage?: string;
    twitterCard?: string;
    twitterTitle?: string;
    twitterDescription?: string;
    twitterImage?: string;
    twitterUrl?: string;
}

interface PreviousHeadState {
    title: string;
    description: string | null;
    canonical: string | null;
    robots: string | null;
    ogTitle: string | null;
    ogDescription: string | null;
    ogUrl: string | null;
    ogType: string | null;
    ogSiteName: string | null;
    ogImage: string | null;
    twitterCard: string | null;
    twitterTitle: string | null;
    twitterDescription: string | null;
    twitterImage: string | null;
    twitterUrl: string | null;
    hadCanonical: boolean;
    hadRobots: boolean;
    hadDescription: boolean;
    hadOgTitle: boolean;
    hadOgDescription: boolean;
    hadOgUrl: boolean;
    hadOgType: boolean;
    hadOgSiteName: boolean;
    hadOgImage: boolean;
    hadTwitterCard: boolean;
    hadTwitterTitle: boolean;
    hadTwitterDescription: boolean;
    hadTwitterImage: boolean;
    hadTwitterUrl: boolean;
}

function getOrCreateMeta(attr: string, value: string): HTMLMetaElement {
    let el = document.querySelector<HTMLMetaElement>(
        `meta[${attr}="${value}"]`,
    );
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, value);
        document.head.appendChild(el);
    }
    return el;
}

function getOrCreateLink(rel: string): HTMLLinkElement {
    let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    return el;
}

function getMetaContent(attr: string, value: string): string | null {
    const el = document.querySelector<HTMLMetaElement>(
        `meta[${attr}="${value}"]`,
    );
    return el ? el.getAttribute("content") : null;
}

function getLinkHref(rel: string): string | null {
    const el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
    return el ? el.getAttribute("href") : null;
}

const useSeo = ({
    title,
    description,
    canonical,
    includeQuery,
    robots,
    ogType,
    ogImage,
    twitterCard,
    twitterTitle,
    twitterDescription,
    twitterImage,
    twitterUrl,
}: SeoOptions): void => {
    const prevStateRef = useRef<PreviousHeadState | null>(null);

    useEffect(() => {
        // Capture previous head state on first mount
        if (prevStateRef.current === null) {
            prevStateRef.current = {
                title: document.title,
                description: getMetaContent("name", "description"),
                canonical: getLinkHref("canonical"),
                robots: getMetaContent("name", "robots"),
                ogTitle: getMetaContent("property", "og:title"),
                ogDescription: getMetaContent("property", "og:description"),
                ogUrl: getMetaContent("property", "og:url"),
                ogType: getMetaContent("property", "og:type"),
                ogSiteName: getMetaContent("property", "og:site_name"),
                ogImage: getMetaContent("property", "og:image"),
                twitterCard: getMetaContent("name", "twitter:card"),
                twitterTitle: getMetaContent("name", "twitter:title"),
                twitterDescription: getMetaContent(
                    "name",
                    "twitter:description",
                ),
                twitterImage: getMetaContent("name", "twitter:image"),
                twitterUrl: getMetaContent("name", "twitter:url"),
                hadCanonical: !!document.querySelector<HTMLLinkElement>(
                    'link[rel="canonical"]',
                ),
                hadRobots: !!document.querySelector<HTMLMetaElement>(
                    'meta[name="robots"]',
                ),
                hadDescription: !!document.querySelector<HTMLMetaElement>(
                    'meta[name="description"]',
                ),
                hadOgTitle: !!document.querySelector<HTMLMetaElement>(
                    'meta[property="og:title"]',
                ),
                hadOgDescription: !!document.querySelector<HTMLMetaElement>(
                    'meta[property="og:description"]',
                ),
                hadOgUrl: !!document.querySelector<HTMLMetaElement>(
                    'meta[property="og:url"]',
                ),
                hadOgType: !!document.querySelector<HTMLMetaElement>(
                    'meta[property="og:type"]',
                ),
                hadOgSiteName: !!document.querySelector<HTMLMetaElement>(
                    'meta[property="og:site_name"]',
                ),
                hadOgImage: !!document.querySelector<HTMLMetaElement>(
                    'meta[property="og:image"]',
                ),
                hadTwitterCard: !!document.querySelector<HTMLMetaElement>(
                    'meta[name="twitter:card"]',
                ),
                hadTwitterTitle: !!document.querySelector<HTMLMetaElement>(
                    'meta[name="twitter:title"]',
                ),
                hadTwitterDescription:
                    !!document.querySelector<HTMLMetaElement>(
                        'meta[name="twitter:description"]',
                    ),
                hadTwitterImage: !!document.querySelector<HTMLMetaElement>(
                    'meta[name="twitter:image"]',
                ),
                hadTwitterUrl: !!document.querySelector<HTMLMetaElement>(
                    'meta[name="twitter:url"]',
                ),
            };
        }

        // Title
        document.title = title;

        // Meta description
        const descMeta = getOrCreateMeta("name", "description");
        descMeta.setAttribute("content", description);

        // Canonical — include query string when requested or when ?page= is present
        let canonicalUrl: string;
        if (canonical) {
            canonicalUrl = canonical;
        } else {
            const search = window.location.search;
            const useSearch =
                includeQuery ||
                (search && new URLSearchParams(search).has("page"));
            canonicalUrl =
                window.location.origin +
                window.location.pathname +
                (useSearch ? search : "");
        }
        const canonicalLink = getOrCreateLink("canonical");
        canonicalLink.setAttribute("href", canonicalUrl);

        // Robots (only when explicitly provided)
        if (robots) {
            const robotsMeta = getOrCreateMeta("name", "robots");
            robotsMeta.setAttribute("content", robots);
        }

        // Open Graph
        const ogTitleMeta = getOrCreateMeta("property", "og:title");
        ogTitleMeta.setAttribute("content", title);

        const ogDescMeta = getOrCreateMeta("property", "og:description");
        ogDescMeta.setAttribute("content", description);

        const ogUrlMeta = getOrCreateMeta("property", "og:url");
        ogUrlMeta.setAttribute("content", canonicalUrl);

        const ogTypeMeta = getOrCreateMeta("property", "og:type");
        ogTypeMeta.setAttribute("content", ogType || "website");

        const ogSiteNameMeta = getOrCreateMeta("property", "og:site_name");
        ogSiteNameMeta.setAttribute("content", SITE_NAME);

        if (ogImage) {
            const ogImageMeta = getOrCreateMeta("property", "og:image");
            ogImageMeta.setAttribute("content", ogImage);
        }

        // Twitter / X
        const resolvedTwitterUrl = twitterUrl || canonicalUrl;
        const resolvedTwitterCard =
            twitterCard ||
            (twitterImage || ogImage ? "summary_large_image" : "summary");

        const twCardMeta = getOrCreateMeta("name", "twitter:card");
        twCardMeta.setAttribute("content", resolvedTwitterCard);

        const twTitleMeta = getOrCreateMeta("name", "twitter:title");
        twTitleMeta.setAttribute("content", twitterTitle || title);

        const twDescMeta = getOrCreateMeta("name", "twitter:description");
        twDescMeta.setAttribute("content", twitterDescription || description);

        const twUrlMeta = getOrCreateMeta("name", "twitter:url");
        twUrlMeta.setAttribute("content", resolvedTwitterUrl);

        const resolvedTwitterImage = twitterImage || ogImage;
        if (resolvedTwitterImage) {
            const twImageMeta = getOrCreateMeta("name", "twitter:image");
            twImageMeta.setAttribute("content", resolvedTwitterImage);
        }

        // Cleanup: restore previous head state
        return () => {
            const prev = prevStateRef.current;
            if (!prev) return;

            // Restore title
            document.title = prev.title || DEFAULT_TITLE;

            // Restore or remove description
            const descEl = document.querySelector<HTMLMetaElement>(
                'meta[name="description"]',
            );
            if (descEl) {
                if (prev.hadDescription && prev.description !== null) {
                    descEl.setAttribute("content", prev.description);
                } else if (!prev.hadDescription) {
                    descEl.parentNode?.removeChild(descEl);
                }
            }

            // Restore or remove canonical
            const canonicalEl = document.querySelector<HTMLLinkElement>(
                'link[rel="canonical"]',
            );
            if (canonicalEl) {
                if (prev.hadCanonical && prev.canonical !== null) {
                    canonicalEl.setAttribute("href", prev.canonical);
                } else if (!prev.hadCanonical) {
                    canonicalEl.parentNode?.removeChild(canonicalEl);
                }
            }

            // Restore or remove robots
            const robotsEl = document.querySelector<HTMLMetaElement>(
                'meta[name="robots"]',
            );
            if (robotsEl) {
                if (prev.hadRobots && prev.robots !== null) {
                    robotsEl.setAttribute("content", prev.robots);
                } else if (!prev.hadRobots) {
                    robotsEl.parentNode?.removeChild(robotsEl);
                }
            }

            // Restore or remove OG tags
            const ogRestore: Array<[string, string | null, boolean]> = [
                ["og:title", prev.ogTitle, prev.hadOgTitle],
                ["og:description", prev.ogDescription, prev.hadOgDescription],
                ["og:url", prev.ogUrl, prev.hadOgUrl],
                ["og:type", prev.ogType, prev.hadOgType],
                ["og:site_name", prev.ogSiteName, prev.hadOgSiteName],
                ["og:image", prev.ogImage, prev.hadOgImage],
            ];
            for (const [prop, value, existed] of ogRestore) {
                const el = document.querySelector<HTMLMetaElement>(
                    `meta[property="${prop}"]`,
                );
                if (el) {
                    if (existed && value !== null) {
                        el.setAttribute("content", value);
                    } else if (!existed) {
                        el.parentNode?.removeChild(el);
                    }
                }
            }

            // Restore or remove Twitter tags
            const twRestore: Array<[string, string | null, boolean]> = [
                ["twitter:card", prev.twitterCard, prev.hadTwitterCard],
                ["twitter:title", prev.twitterTitle, prev.hadTwitterTitle],
                [
                    "twitter:description",
                    prev.twitterDescription,
                    prev.hadTwitterDescription,
                ],
                ["twitter:image", prev.twitterImage, prev.hadTwitterImage],
                ["twitter:url", prev.twitterUrl, prev.hadTwitterUrl],
            ];
            for (const [name, value, existed] of twRestore) {
                const el = document.querySelector<HTMLMetaElement>(
                    `meta[name="${name}"]`,
                );
                if (el) {
                    if (existed && value !== null) {
                        el.setAttribute("content", value);
                    } else if (!existed) {
                        el.parentNode?.removeChild(el);
                    }
                }
            }
        };
    }, [
        title,
        description,
        canonical,
        includeQuery,
        robots,
        ogType,
        ogImage,
        twitterCard,
        twitterTitle,
        twitterDescription,
        twitterImage,
        twitterUrl,
    ]);
};

export default useSeo;
